import { Component, type ErrorInfo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { track } from '@/utils/analytics'
import { isReloading, isStaleChunkError } from '@/utils/lazyRoute'

import styles from './AppErrorBoundary.module.scss'

/**
 * ЭКРАН СБОЯ ВМЕСТО ПУСТОТЫ.
 *
 * До 2026-09-22 в приложении не было ни одной границы ошибок, и любое исключение при рендере
 * размонтировало весь корень: белая страница, ноль символов, ни одного выхода. Так выглядел сбой
 * воронки 3 после контакта (пропавший чанк, см. utils/lazyRoute.ts), и так же выглядел бы любой
 * другой — человек не мог отличить «сломалось» от «грузится» и уходил.
 *
 * Граница ловит всё, что не вылечилось само: чанк, пропавший дважды подряд, и настоящие ошибки
 * рендера. Она говорит одно — что именно случилось и что введённое сохранено — и даёт одно действие,
 * перезагрузку: хранилище переживает её, а сбой, вызванный пересборкой, она лечит.
 *
 * Класс, а не хук: перехватывать ошибки рендера умеет только классовый компонент (React не дал для
 * этого хука). Состояние здесь — одна булевка самого React, стор для неё был бы церемонией.
 * `resetKey` — путь страницы: переход на другой маршрут снимает экран сбоя, иначе человек, ушедший
 * по ссылке, остался бы под ним.
 */
type Props = { children: ReactNode; resetKey: string }
type State = { failed: boolean; stale: boolean; key: string }

const CrashScreen = ({ stale }: { stale: boolean }) => {
  const { t } = useTranslation()
  return (
    <div className={styles.root} role="alert">
      <div className={styles.card}>
        <b className={styles.title}>{stale ? t('ui.crash.staleTitle') : t('ui.crash.title')}</b>
        <p className={styles.text}>{stale ? t('ui.crash.staleText') : t('ui.crash.text')}</p>
        <button type="button" className={styles.reload} onClick={() => window.location.reload()}>
          {t('ui.crash.reload')}
        </button>
      </div>
    </div>
  )
}

export class AppErrorBoundary extends Component<Props, State> {
  override state: State = { failed: false, stale: false, key: this.props.resetKey }

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { failed: true, stale: isStaleChunkError(error) }
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    return props.resetKey !== state.key ? { failed: false, stale: false, key: props.resetKey } : null
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    /* Страница уже уходит на перезагрузку, которая лечит пропавший чанк: всё, что упало в эту долю
       секунды, — следствие той же пропажи, а не новая поломка, и считать его сбоем значило бы
       записать каждую удачную перезагрузку ещё и крахом (так делала первая версия, см. lazyRoute). */
    if (isReloading()) return
    /* Текст ошибки — в консоль для отладки, в аналитику — только класс сбоя и маршрут: сообщение
       исключения может нести куски данных строки, и в Amplitude ему не место. */
    console.error('[app] render failed', error, info.componentStack)
    track('app_crashed', { kind: isStaleChunkError(error) ? 'stale_chunk_twice' : 'render', path: window.location.pathname })
  }

  override render() {
    /* Во время лечебной перезагрузки — ничего, а не экран сбоя: он был бы вспышкой на доли секунды. */
    if (this.state.failed && isReloading()) return null
    return this.state.failed ? <CrashScreen stale={this.state.stale} /> : this.props.children
  }
}
